import { createServerFn } from "@tanstack/react-start";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { excerptFromContent, slugify } from "@/lib/blog-meta";
import { getServerSupabase } from "@/lib/supabase.server";

export interface BlogPostSummary {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  cover_image_url: string | null;
  published_at: string | null;
}

export interface BlogPost extends BlogPostSummary {
  content: string;
  status: string;
  created_at: string;
  updated_at: string;
}

/** The editor only ever sends the subject and the text; everything else is derived. */
export interface BlogPostInput {
  title: string;
  content: string;
  status: "draft" | "published";
}

const SUMMARY_COLUMNS = "id,title,slug,excerpt,cover_image_url,published_at";

export const listPublishedPosts = createServerFn({ method: "GET" }).handler(
  async (): Promise<BlogPostSummary[]> => {
    const supabase = getServerSupabase();
    const { data, error } = await supabase
      .from("blog_posts")
      .select(SUMMARY_COLUMNS)
      .eq("status", "published")
      .lte("published_at", new Date().toISOString())
      .order("published_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  },
);

export const getPostBySlug = createServerFn({ method: "GET" })
  .validator((slug: string) => slug)
  .handler(async ({ data: slug }): Promise<BlogPost | null> => {
    const supabase = getServerSupabase();
    const { data, error } = await supabase
      .from("blog_posts")
      .select("*")
      .eq("slug", slug)
      .eq("status", "published")
      .lte("published_at", new Date().toISOString())
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data;
  });

async function requireAdmin(
  supabase: Pick<ReturnType<typeof getServerSupabase>, "rpc">,
  userId: string,
) {
  const { data: isAdmin } = await supabase.rpc("has_role", { _user_id: userId, _role: "admin" });
  if (!isAdmin) throw new Error("forbidden");
}

/** Builds an address like /blog/como-crescer-com-consistencia, adding -2, -3… on a clash. */
async function uniqueSlug(
  supabase: ReturnType<typeof getServerSupabase>,
  base: string,
): Promise<string> {
  const root = base || "post";
  for (let i = 1; i <= 50; i++) {
    const candidate = i === 1 ? root : `${root}-${i}`;
    const { data } = await supabase
      .from("blog_posts")
      .select("id")
      .eq("slug", candidate)
      .maybeSingle();
    if (!data) return candidate;
  }
  return `${root}-${Date.now()}`;
}

export const listAllPosts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<BlogPost[]> => {
    await requireAdmin(context.supabase, context.userId);
    const { data, error } = await context.supabase
      .from("blog_posts")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const createPost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((input: BlogPostInput) => input)
  .handler(async ({ context, data: input }) => {
    await requireAdmin(context.supabase, context.userId);
    const title = input.title.trim();
    if (!title) throw new Error("Preencha o título do post.");
    const slug = await uniqueSlug(context.supabase, slugify(title));
    const { error } = await context.supabase.from("blog_posts").insert({
      title,
      slug,
      excerpt: excerptFromContent(input.content),
      content: input.content,
      cover_image_url: null,
      status: input.status,
      published_at: input.status === "published" ? new Date().toISOString() : null,
    });
    if (error) throw new Error(error.message);
    return { ok: true, slug };
  });

export const updatePost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((input: BlogPostInput & { id: string }) => input)
  .handler(async ({ context, data: input }) => {
    await requireAdmin(context.supabase, context.userId);
    const title = input.title.trim();
    if (!title) throw new Error("Preencha o título do post.");
    const { data: current } = await context.supabase
      .from("blog_posts")
      .select("published_at,slug")
      .eq("id", input.id)
      .maybeSingle();
    if (!current) throw new Error("Post não encontrado.");
    // The address never changes on edit, so links already shared keep working.
    const { error } = await context.supabase
      .from("blog_posts")
      .update({
        title,
        excerpt: excerptFromContent(input.content),
        content: input.content,
        status: input.status,
        updated_at: new Date().toISOString(),
        published_at:
          input.status === "published" && !current.published_at
            ? new Date().toISOString()
            : (current.published_at ?? null),
      })
      .eq("id", input.id);
    if (error) throw new Error(error.message);
    return { ok: true, slug: current.slug };
  });

export const deletePost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((id: string) => id)
  .handler(async ({ context, data: id }) => {
    await requireAdmin(context.supabase, context.userId);
    const { error } = await context.supabase.from("blog_posts").delete().eq("id", id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
